import { useEffect } from 'react';
import { useGrafanaFaro as useGrafanaFaroContext } from '@/contexts/GrafanaFaroContext';
import type { User } from '@/types';

/**
 * Хук для работы с Grafana Faro
 *
 * @example
 * const { pushEvent, pushError, setUser } = useGrafanaFaro();
 *
 * // Track custom event
 * pushEvent('button_clicked', { button: 'submit', page: 'dashboard' });
 *
 * // Track error
 * try {
 *   await someOperation();
 * } catch (error) {
 *   pushError(error as Error, { operation: 'someOperation' });
 * }
 */
export const useGrafanaFaro = () => {
  const faro = useGrafanaFaroContext();

  return {
    ...faro,

    // Convenience methods
    trackPageView: (pageName: string, attributes?: Record<string, unknown>) => {
      faro.pushEvent('page_view', { pageName, ...attributes });
    },

    trackUserAction: (action: string, attributes?: Record<string, unknown>) => {
      faro.pushEvent(`user_action_${action}`, attributes);
    },

    trackError: (error: Error | unknown, context?: Record<string, unknown>) => {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      faro.pushError(errorObj, context);
    },
  };
};

/**
 * Хук для автоматического отслеживания user ID в Faro
 *
 * Используется внутри компонентов для синхронизации auth state с Faro
 *
 * @example
 * const FaroUserTracker = () => {
 *   const { user } = useUnifiedAuth();
 *   useFaroUserTracking(user);
 *   return null;
 * };
 */
export const useFaroUserTracking = (user: User | null) => {
  const { setUser, resetUser } = useGrafanaFaro();

  useEffect(() => {
    if (user) {
      setUser(user);
    } else {
      resetUser();
    }
  }, [user, setUser, resetUser]);
};
